import * as React from "react";

import { ExtendedFTNode } from "typings/flexFramework/FlexComponent";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";

import { ClientWizardStateStore } from "stores/wizard/ClientWizardStateStore";

import { HeroImage } from "components/shared/StorefrontWizard/HeroImage/HeroImage";
import {
  TabbedWizardCard,
  WizardRegionType,
} from "components/shared/StorefrontWizard/TabbedWizardCard/TabbedWizardCard";
import { LobQueryRouter } from "components/shared/StorefrontWizard/LobQueryRouter";
import { Viewport, ViewSmall, ViewMedium } from "@shared-ui/viewport-context";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkHeading, UitkSubheading } from "uitk-react-text";

export interface WizardHeroProps {
  wizardLOBs: ExtendedFTNode[];
  wizardFormChildren: React.ReactElement[];
  trackOnChangeTab: (tabName: string, rfrrId?: string) => void;
  context: ExtendedContextStore;
  heroImageFigureClassNames?: string;
  heroImageSrc: string;
  heroImageColor?: string;
  wizardState: ClientWizardStateStore;
  cdgTreatment?: boolean;
  aboveOtherFormChildren?: React.ReactElement[];
  belowOtherFormChildren?: React.ReactElement[];
  heading?: string;
  subHeading?: string;
}

export const WizardHero: React.FC<WizardHeroProps> = ({
  wizardLOBs,
  wizardFormChildren,
  trackOnChangeTab,
  context,
  heroImageFigureClassNames,
  heroImageSrc,
  heroImageColor,
  wizardState,
  aboveOtherFormChildren,
  belowOtherFormChildren,
  cdgTreatment,
  heading,
  subHeading,
}) => {
  const customStyle = heroImageColor ? { backgroundColor: heroImageColor } : undefined;

  const wizardHeroContent = (
    <>
      {cdgTreatment && <UitkScrim type={UitkScrimType.OVERLAY} />}
      {heading && (
        <UitkSpacing
          className="SimpleContainer cdgHeading"
          padding={{ inline: "six", blockstart: "six", small: { blockend: "six" }, medium: { blockend: "unset" } }}
        >
          <div>
            <Viewport>
              <ViewSmall>
                <UitkHeading className="cdgHeadingText" tag="h1" size={cdgTreatment ? 4 : 2}>
                  {heading}
                </UitkHeading>
              </ViewSmall>
              <ViewMedium>
                <UitkHeading className="cdgHeadingText" tag="h1" size={2}>
                  {heading}
                </UitkHeading>
              </ViewMedium>
            </Viewport>
            {subHeading && (
              <UitkSubheading className="cdgHeadingText" tag="h2">
                {subHeading}
              </UitkSubheading>
            )}
          </div>
        </UitkSpacing>
      )}

      {aboveOtherFormChildren}
      <LobQueryRouter lobs={wizardLOBs} />
      <TabbedWizardCard
        context={context}
        wizardFormChildren={wizardFormChildren}
        wizardState={wizardState}
        wrappingClassNames="SimpleContainer"
        wrappingSpacing={{
          padding: { medium: { inline: "six", blockstart: heading ? "six" : "twelve", blockend: "four" } },
        }}
        tabGroupClassNames="formAlignedTabs"
        wizardFormClassNames="wizardOverHeroImage"
        wizardFormSpacing={{ padding: { inline: "six" } }}
        trackOnChangeTab={trackOnChangeTab}
        wizardRegionType={WizardRegionType.BEX}
        hasBorder={!heroImageSrc}
      />
      {belowOtherFormChildren}
    </>
  );

  return (
    <div style={customStyle}>
      {heroImageSrc ? (
        <HeroImage className={heroImageFigureClassNames} src={heroImageSrc}>
          {wizardHeroContent}
        </HeroImage>
      ) : (
        wizardHeroContent
      )}
    </div>
  );
};
